import { useCallback } from 'react'
import { useLibraryAdapter } from '../data/DataAdapterProvider'

/**
 * Thin facade over the active LibraryAdapter so existing call sites
 * (MovieDetailModal, SeriesDetailModal, PlayerModal, etc.) keep their
 * familiar useWriteLibrary().updateMovie() / .setEpisodeWatched() /
 * etc. signatures. Long term these call sites can switch to using the
 * adapter directly, but moving them all in one PR would be churn for
 * no benefit.
 */
export function useWriteLibrary() {
  const adapter = useLibraryAdapter()

  const addMovieToLibrary = useCallback(
    (tmdbId: number) => adapter.addMovie(tmdbId),
    [adapter]
  )
  const removeMovieFromLibrary = useCallback(
    (tmdbId: number) => adapter.removeMovie(tmdbId),
    [adapter]
  )
  const updateMovie = useCallback(
    (tmdbId: number, changes: Parameters<typeof adapter.updateMovie>[1]) =>
      adapter.updateMovie(tmdbId, changes),
    [adapter]
  )

  const addSeriesToLibrary = useCallback(
    (tmdbId: number) => adapter.addSeries(tmdbId),
    [adapter]
  )
  const removeSeriesFromLibrary = useCallback(
    (tmdbId: number) => adapter.removeSeries(tmdbId),
    [adapter]
  )
  const updateSeries = useCallback(
    (tmdbId: number, changes: Parameters<typeof adapter.updateSeries>[1]) =>
      adapter.updateSeries(tmdbId, changes),
    [adapter]
  )

  const setEpisodeWatched = useCallback(
    (
      seriesTmdbId: number,
      seasonNumber: number,
      episodeNumber: number,
      watched: boolean
    ) => adapter.setEpisodeWatched(seriesTmdbId, seasonNumber, episodeNumber, watched),
    [adapter]
  )

  const markWatchedUntil = useCallback(
    (
      seriesTmdbId: number,
      seasons: { seasonNumber: number; episodeNumbers: number[] }[],
      targetSeason: number,
      targetEpisode: number
    ) => adapter.markWatchedUntil(seriesTmdbId, seasons, targetSeason, targetEpisode),
    [adapter]
  )

  return {
    addMovieToLibrary,
    addSeriesToLibrary,
    removeMovieFromLibrary,
    removeSeriesFromLibrary,
    updateMovie,
    updateSeries,
    setEpisodeWatched,
    markWatchedUntil,
  }
}
